import { useTranslation } from 'react-i18next';

/**
 * i18n lookup for recipe / input / option labels that come from the
 * backend seed (recipes.seed.ts in pms-api-v2). The seed is authored in
 * English; this hook lets each component translate those strings on the
 * frontend when a matching key exists in the locale bundle, and falls
 * back to the English seed label otherwise.
 *
 * Key naming:
 *   CONTENT_STUDIO.SEED.RECIPE.<SLUG>.NAME          / .DESCRIPTION
 *   CONTENT_STUDIO.SEED.INPUT.<INPUT_KEY>.LABEL
 *   CONTENT_STUDIO.SEED.OPTION.<INPUT_KEY>.<VALUE>
 *
 * Slugs / input keys / values are SCREAMING_SNAKE_CASE in the key path
 * (hyphens, spaces, and dots become underscores) — see normalize(...).
 *
 * Missing keys fall through to the passed fallback, so components don't
 * need to know in advance which labels have a translation. Adding a
 * translation for a new label is just "add the key to en/fr/es/ar";
 * code doesn't change.
 */

function normalize(s: string): string {
    // Split camelCase FIRST (backdropColor → backdrop_Color) so keys
    // like 'aspectRatio', 'cameraHeight', 'backdropColor' match their
    // SCREAMING_SNAKE_CASE counterparts in the i18n bundle. Without
    // this, 'backdropColor' flattened to 'BACKDROPCOLOR' and missed
    // the BACKDROP_COLOR key entirely.
    return String(s)
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
}

export function useSeedI18n() {
    const { t, i18n } = useTranslation();
    const has = (k: string): boolean => i18n.exists(k);

    return {
        recipeName(slug: string, fallback: string): string {
            const k = `CONTENT_STUDIO.SEED.RECIPE.${normalize(slug)}.NAME`;
            return has(k) ? (t(k) as string) : fallback;
        },
        recipeDescription(slug: string, fallback: string): string {
            const k = `CONTENT_STUDIO.SEED.RECIPE.${normalize(slug)}.DESCRIPTION`;
            return has(k) ? (t(k) as string) : fallback;
        },
        inputLabel(inputKey: string, fallback: string): string {
            const k = `CONTENT_STUDIO.SEED.INPUT.${normalize(inputKey)}.LABEL`;
            return has(k) ? (t(k) as string) : fallback;
        },
        optionLabel(inputKey: string, value: any, fallback: string): string {
            if (value === undefined || value === null || value === '') return fallback;
            const k = `CONTENT_STUDIO.SEED.OPTION.${normalize(inputKey)}.${normalize(value)}`;
            return has(k) ? (t(k) as string) : fallback;
        },
    };
}
